import {
    ButtonGradientPrimary,
    Card,
    Colors,
    FOCUS_RING,
    Heading,
    Icon,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';
import type { ReactNode } from 'react';

import { RocketIllustration } from '@/components/Illustrations/Illustration';
import { useAmelioTranslations } from '@/hooks';

type LaunchReviewTranslationKeys = {
    titleKey?: TranslationKey;
    ctaKey: TranslationKey;
};

interface LaunchReviewProps {
    onLaunchReview: () => void;
    isLaunching?: boolean;
    translationKeys?: LaunchReviewTranslationKeys;
    note?: ReactNode;
    disabled?: boolean;
    disabledReason?: string;
}

const defaultTranslationKeys: LaunchReviewTranslationKeys = {
    titleKey: 'evaluation360ScheduleCreation.launchReview.title',
    ctaKey: 'evaluation360ScheduleCreation.launchReview.launch',
};

const LaunchReview = ({
    onLaunchReview,
    isLaunching = false,
    translationKeys = defaultTranslationKeys,
    note,
    disabled = false,
    disabledReason,
}: LaunchReviewProps) => {
    const { t } = useAmelioTranslations();

    const isWithdrawn = isLaunching || disabled;
    const hasReason = !!disabledReason && disabled;

    const launchButton = (
        <ButtonGradientPrimary
            className={hasReason ? 'pointer-events-none my-5' : 'my-5'}
            onClick={onLaunchReview}
            disabled={isWithdrawn}
            loading={isLaunching}
        >
            <Icon
                name={'design-designtools'}
                color={Colors.WHITE}
                className={'mr-2'}
            />

            {t(translationKeys.ctaKey)}
        </ButtonGradientPrimary>
    );

    return (
        <Card
            className={
                'gradient-border-secondary-to-primary relative mt-5 flex flex-col items-center'
            }
            variant={'flat'}
            borderRadius={'2xl'}
        >
            <RocketIllustration
                className={
                    'absolute -top-[80px] right-0 w-30! md:top-auto md:right-auto md:left-[-40px] md:w-40! md:max-xl:!-top-[50px]'
                }
            />

            {translationKeys.titleKey && (
                <Heading
                    className={'mt-5 text-center md:text-left!'}
                    level={3}
                >
                    {t(translationKeys.titleKey)}
                </Heading>
            )}

            {hasReason ? (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                tabIndex={0}
                                className={`inline-flex ${FOCUS_RING}`}
                                data-testid={'launch-review-disabled-reason'}
                            >
                                {launchButton}
                            </span>
                        </TooltipTrigger>

                        <TooltipContent
                            side={'top'}
                            className={'max-w-xs break-words whitespace-normal'}
                        >
                            {disabledReason}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                launchButton
            )}

            {note && (
                <Text
                    color={Colors.SLATE}
                    className={'mb-5 text-center'}
                >
                    {note}
                </Text>
            )}
        </Card>
    );
};

export { LaunchReview };
export type { LaunchReviewProps, LaunchReviewTranslationKeys };
